import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Sparkles, History, Plus, ClipboardList, X } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import { useVoiceControl } from "@/hooks/useVoiceControl";
import { useAIChatStore, ChatMessage as StoreChatMessage } from "@/stores/ai/ai-chat-store";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { AlertCircle } from "lucide-react";
import { useActionCardInjection } from "./action-cards/useActionCardInjection";
import { cn } from "@/lib/utils";
import { GuidedFlowSchema } from "@/schemas/storage";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { useDebugStore } from "@/stores/ai/debug-store";
import { Component, ComponentInput, ComponentType, DataTextProperties } from "@/types";
import { useSlashCommands } from '@/services/ai/commands/useSlashCommands';
import { slashCommandSystem } from '@/services/ai/commands/SlashCommandSystem';
import { useSpecializationScope as useTradeScope } from '@/stores/service-provider/engagement-store';
import { applyAgentResponse } from '@/services/editor/CanvasActionApplicator';
import { visualEditorApi } from '@/services/api/agentApi';
import { shouldClearHomeAssistantPlaceholderAction } from '@/services/homeAssistant/binding';
// Phase 3: Streaming & Decomposition
import * as agentConversationManager from '@/services/editor/AgentConversationManager';
import { StreamingProgressIndicator } from './StreamingProgressIndicator';
import { AgentFeedbackPanel } from './AgentFeedbackPanel';

import { ProactiveSuggestionPanel } from '@/components/editor/ProactiveSuggestionPanel';
import { type ResolvedMessage, type PageContext } from '@/services/ai/mentions';

// Plan Mode
import { usePlanStore, useIsAtCheckpoint } from '@/stores/ai/plan-store';
import { usePlanOrchestration } from '@/hooks/ai/usePlanOrchestration';
import { useAIChatState } from '@/hooks/ai/useAIChatState';
import { useAgentStatus } from '@/hooks/ai/useAgentStatus';
import { useConversationPersistence } from '@/hooks/ai/useConversationPersistence';
import { useMentionInput } from '@/hooks/ai/useMentionInput';
import { useClarificationFlow } from '@/hooks/ai/useClarificationFlow';
import { useAgentMessaging } from '@/hooks/ai/useAgentMessaging';
import { ChatMessageList } from './ChatMessageList';
import { ChatInputBar } from './ChatInputBar';
import { PlanModePanel } from './PlanModePanel';
import { AIChatModals } from './AIChatModals';
import { PIPELINE_STAGE_LABELS } from '@/types/plan';
import { PropertyMessage } from "./types";
import { useDataDiscovery } from "./useDataDiscovery";
import { useStyleIntelligence } from "./useStyleIntelligence";
import { useCommunitiesStore } from "@/stores/agents/communities-store";
import { useKnowledgeStore } from "@/stores/knowledge/knowledge-store";
import { useMission } from "./MissionModal";
import { useChartTitleEventListener } from "@/hooks/useAutoChartTitle";
import type { ColorSchemeWriteTarget } from "@/types/color-scheme";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isHTMLElement = (value: unknown): value is HTMLElement =>
  value instanceof HTMLElement;

const DATA_TEXT_SECONDARY_FIELDS = new Set(["name", "date", "time", "timestamp", "bucket"]);

const getComponentName = (component: Component): string =>
  component.name.toLowerCase();

const getDataTextValueField = (component: Component): string | undefined => {
  const valueField = component.properties.valueField;
  return typeof valueField === "string" ? valueField : undefined;
};

const getDataTextValueFormatter = (component: Component): string | undefined => {
  const valueFormatter = component.properties.valueFormatter;
  return typeof valueFormatter === "string" ? valueFormatter : undefined;
};

const getBaseStyle = (component: Component): Record<string, unknown> => {
  const baseStyle = component.style.base;
  return isRecord(baseStyle) ? baseStyle : component.style;
};

const getStyleFontSize = (component: Component): number | null => {
  const fontSize = getBaseStyle(component).fontSize;
  if (typeof fontSize === "number" && Number.isFinite(fontSize)) {
    return fontSize;
  }
  if (typeof fontSize === "string") {
    const parsed = Number.parseFloat(fontSize);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const isSecondaryChartDataText = (component: Component, dateField: string): boolean => {
  const name = getComponentName(component);
  const valueField = getDataTextValueField(component);
  const formatter = getDataTextValueFormatter(component);
  const fontSize = getStyleFontSize(component);

  return (
    name.includes("date") ||
    name.includes("time") ||
    name.includes("x-axis") ||
    name.includes("axis") ||
    valueField === dateField ||
    (valueField !== undefined && DATA_TEXT_SECONDARY_FIELDS.has(valueField)) ||
    formatter === "date" ||
    formatter === "time" ||
    formatter === "datetime" ||
    (fontSize !== null && fontSize <= 14)
  );
};

const getChartSeriesFieldName = (field: string): string =>
  field
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getDataTextProperties = (component: Component | undefined): DataTextProperties => {
  if (!component) {
    return {};
  }
  return component.properties;
};

const createSeriesDataTextInput = ({
  base,
  chartId,
  field,
  id,
  parentId,
  pageId,
  index,
}: {
  base: Component | undefined;
  chartId: string;
  field: string;
  id: string;
  parentId: string;
  pageId: string;
  index: number;
}): ComponentInput => ({
  id,
  type: ComponentType.DATA_TEXT,
  name: `Series ${index + 1} Value Display`,
  pageId,
  parentId,
  properties: {
    ...getDataTextProperties(base),
    linkedChartId: chartId,
    valueField: field,
    hoverValueField: field,
    showCurrentOnIdle: true,
    value: "--",
  },
  position: {
    x: base?.position?.x ?? 0,
    y: base?.position?.y ?? 0,
    z: (base?.position?.z ?? 4) + index,
  },
  style: base?.style ?? {
    base: {
      width: "auto",
      height: "auto",
      color: "hsl(var(--muted-foreground))",
      fontSize: "28px",
      fontWeight: 500,
      textAlign: "right",
    },
  },
  responsivePosition: base?.responsivePosition,
});

const createDateDataTextInput = ({
  base,
  chartId,
  dateField,
  id,
  parentId,
  pageId,
}: {
  base: Component | undefined;
  chartId: string;
  dateField: string;
  id: string;
  parentId: string;
  pageId: string;
}): ComponentInput => {
  const baseStyle = base?.style ?? {};
  const nestedBaseStyle = isRecord(baseStyle.base) ? baseStyle.base : {};

  return {
    id,
    type: ComponentType.DATA_TEXT,
    name: "X Axis Display",
    pageId,
    parentId,
    properties: {
      ...getDataTextProperties(base),
      linkedChartId: chartId,
      valueField: dateField,
      hoverValueField: undefined,
      showCurrentOnIdle: true,
      animateValueChange: false,
      value: "--",
    },
    position: {
      x: base?.position?.x ?? 0,
      y: base?.position?.y ?? 0,
      z: base?.position?.z ?? 5,
    },
    style: {
      ...baseStyle,
      base: {
        ...nestedBaseStyle,
        width: "auto",
        height: "auto",
        color: "hsl(var(--muted-foreground))",
        fontSize: "12px",
        fontWeight: 400,
        textAlign: "right",
      },
    },
    responsivePosition: base?.responsivePosition,
  };
};

export interface AIPropertiesChatProps {
  component?: Component | null;
  multipleComponents?: Component[];
  isCanvas?: boolean;
  className?: string;
  onOpenMapTemplates?: () => void;
}


export const AIPropertiesChat: React.FC<AIPropertiesChatProps> = ({
  component,
  multipleComponents,
  isCanvas = false,
  className,
  onOpenMapTemplates,
}) => {
  // Subscribe to chart title events for auto-title generation
  useChartTitleEventListener();

  // Specialization scope for service provider context
  const tradeScope = useTradeScope();

  // Voice control
  const {
    voiceState,
    showEnrollment,
    setShowEnrollment,
    error: voiceError,
    handleMicPress,
    handleStopSpeaking,
    handleSpeakResponse,
    onEnrollmentComplete,
    setOnTranscription,
  } = useVoiceControl();

  const { tokens } = useAuthStore();
  const {
    updateComponent,
    resizeComponent,
    duplicateComponent,
    canvasSettings,
    updateCanvasBackground,
    updateGridSettings,
    updateCanvasLayout,
    updateCanvasTheme,
    updateResponsiveSettings,
    addComponent,
    project,
    moveComponent,
    selectMultipleComponents,
    generatePageFromDescription,
    aiGenerationStage,
    aiGenerationError,
    aiGenerationMetadata,
    resetAIGenerationState,
    components,
    getComponent,
    pages: storePages,
    currentPageId,
  } = useEditorStore();
  const { userCommunity, fetchUserCommunity } = useCommunitiesStore();
  const [messages, setMessages] = useState<PropertyMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const handleSendMessageRef = useRef<(() => Promise<void> | void) | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const openKnowledgeModal = useKnowledgeStore((s) => s.openModal);
  const platformAdminEntitlement = useOrganizationStore(
    (state) => state.platformAdminEntitlement,
  );
  // Plan Mode state
  const planStore = usePlanStore();
  const isFullMode = planStore.pipelineMode === 'full';
  const isAtCheckpoint = useIsAtCheckpoint();
  // Consolidated pure state: streaming, modals, feedback, suggestions, skills
  const {
    // streaming state
    streamingSteps, setStreamingSteps,
    streamingMessage, setStreamingMessage,
    isStreamingActive, setIsStreamingActive,
    multiPageProgress, setMultiPageProgress,
    pendingConfirmation, setPendingConfirmation,
    pendingChangeSummary, setPendingChangeSummary,
    streamingActionCountRef,
    pendingConfirmationActionsRef,
    // modal state
    conversationPopoverOpen, setConversationPopoverOpen,
    showSkillsModal, setShowSkillsModal,
    showAgentsModal, setShowAgentsModal,
    showDebugSuite, setShowDebugSuite,
    roadmapModalOpen, setRoadmapModalOpen,
    detailsModalOpen, setDetailsModalOpen,
    iconPickerOpen, setIconPickerOpen,
    colorPickerOpen, setColorPickerOpen,
    colorCommandPrefix, setColorCommandPrefix,
    colorPropertyLabel, setColorPropertyLabel,
    deviceBindingOpen, setDeviceBindingOpen,
    homeAssistantBindingOpen, setHomeAssistantBindingOpen,
    removeDataModalOpen, setRemoveDataModalOpen,
    // feedback
    messageFeedback, setMessageFeedback,
    // proactive suggestions
    proactiveSuggestions, setProactiveSuggestions,
    // skills
    handleSkillInsert,
  } = useAIChatState({ setInputValue, handleSendMessageRef });

  // Auto-open roadmap modal when pipeline reaches reviewing with a roadmap
  useEffect(() => {
    if (isFullMode && planStore.roadmap && planStore.pipelineStage === 'reviewing' && !planStore.phases.length) {
      setRoadmapModalOpen(true);
    }
  }, [isFullMode, planStore.roadmap, planStore.pipelineStage, planStore.phases.length, setRoadmapModalOpen]);

  // Stable selectors for the auto-save effect
  const roadmapId = usePlanStore(s => s.roadmap?.id);
  const planPipelineStage = usePlanStore(s => s.pipelineStage);
  const saveCurrentRoadmap = usePlanStore(s => s.saveCurrentRoadmap);

  // Auto-save roadmap to backend when generated
  useEffect(() => {
    if (roadmapId && planPipelineStage === 'reviewing') {
      saveCurrentRoadmap();
    }
  }, [roadmapId, planPipelineStage, saveCurrentRoadmap]);

  const [isFocused, setIsFocused] = useState(false);
  // Mention autocomplete (managed by useMentionInput)
  const {
    showMentionAutocomplete, setShowMentionAutocomplete,
    activeMentionContext, setActiveMentionContext,
    cursorPosition, setCursorPosition,
    handleSelectMention,
    detectAndUpdateMention,
  } = useMentionInput({ inputValue, setInputValue, inputRef });
  // Auto-send ref for action card commands
  const pendingAutoSendRef = useRef<string | null>(null);
  const pendingAutoSendStyleWriteTargetRef = useRef<ColorSchemeWriteTarget | null>(null);

  // Wire up voice transcription to auto-send
  useEffect(() => {
    setOnTranscription((text: string) => {
      pendingAutoSendRef.current = text;
      setInputValue(text);
    });
  }, [setOnTranscription]);

  // Enable debug store for platform-admin entitlement.
  useEffect(() => {
    if (platformAdminEntitlement) {
      useDebugStore.getState().enable();
    } else {
      useDebugStore.getState().disable();
    }
  }, [platformAdminEntitlement]);

  // Phase 3: Store current mention context for component creation handlers
  const currentMentionContextRef = useRef<{
    resolvedMessage: ResolvedMessage | null;
    pageContext: PageContext | null;
  }>({ resolvedMessage: null, pageContext: null });
  const { isSlashCommand, executeSlashCommand } = useSlashCommands();
  const [currentGenerationMessageId, setCurrentGenerationMessageId] = useState<string | null>(null);

  // Phase 2: Agent status awareness (managed by useAgentStatus)
  const { agentStatus, setAgentStatus } = useAgentStatus();

  // Note: Keyboard shortcuts (Cmd+Z/Ctrl+Z) are handled by the toolbar globally.
  // Text commands ("undo", "redo") are handled in handleSendMessage below.

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [inputValue]);

  // AI Chat Store integration for persistence
  const {
    activeConversationId,
    messages: storeMessages,
    initialize: initializeStore,
    createConversation,
    addMessage: addStoreMessage,
    syncMessages,
    loadConversations,
    resumeActiveConversation,
    clearActiveConversation,
    // Phase 1.2: Clarification state
    pendingClarification,
    setPendingClarification,
    clearPendingClarification,
    addClarificationToHistory,
    // Command History (recording only)
    addToHistory,
    getLastSuccessfulCommand,
    // Conversation management
    conversations,
    switchConversation,
  } = useAIChatStore();

  // Conversation persistence (store init, thread ID localStorage, message sync)
  const {
    storeInitialized,
    agentThreadIdRef,
    agentVisualThreadIdRef,
    userAgentThreadIdRef,
    resetAgentThread,
  } = useConversationPersistence({
    tokens,
    projectId: project?.id,
    initializeStore,
    loadConversations,
    resumeActiveConversation,
    clearActiveConversation,
    syncMessages,
    fetchUserCommunity,
    activeConversationId,
    storeMessages,
    messages,
    setMessages,
  });

  // Agent status effects moved to useAgentStatus hook

  // Generate message ID helper
  const generateMessageId = () => `msg_${Date.now()}_${Math.random().toString(36).substring(2)}`;

  // Modal state (iconPickerOpen, colorPickerOpen, colorCommandPrefix, colorPropertyLabel,
  // deviceBindingOpen, removeDataModalOpen) is managed by useAIChatState above.

  const removeDataSourceLabel = useMemo(() => {
    if (!component) return null;
    const props = component.properties;
    const dsRaw = props.dataSource;
    const ds = isRecord(dsRaw) ? dsRaw : null;
    if (ds?.type === 'query-template') {
      const qdRaw = ds.queryDefinition;
      const qd = isRecord(qdRaw) ? qdRaw : null;
      return (typeof qd?.schemaName === 'string' ? qd.schemaName : '') || 'Query Template';
    }
    if (ds?.type === 'workflow') {
      const wcRaw = ds.workflowConfig;
      const wc = isRecord(wcRaw) ? wcRaw : null;
      return (typeof wc?.workflowName === 'string' ? wc.workflowName : '') || 'Workflow';
    }
    if (ds?.type === 'composite-query') return 'Composite Query';
    const binding = isRecord(props.binding) ? props.binding : null;
    if (binding?.source === 'home-assistant') {
      return typeof binding.entityId === 'string'
        ? `Home Assistant ${binding.entityId}`
        : 'Home Assistant entity';
    }
    if (ds?.type) return String(ds.type);
    if (props?.queryTemplateId) return 'Query Template';
    if (props?.valueDataSource) return 'Data Source';
    return null;
  }, [component]);

  const handleRemoveDataConfirm = useCallback(() => {
    if (!component) return;
    const props = component.properties;
    const shouldClearHomeAssistantAction =
      shouldClearHomeAssistantPlaceholderAction(props);
    // Set data-connection properties to undefined (not delete) because
    // the editor store shallow-merges properties: { ...existing, ...updates }
    // so deleted keys would be re-introduced from the existing component.
    const cleaned: Record<string, unknown> = {
      ...props,
      dataSource: undefined,
      binding: undefined,
      entityId: undefined,
      queryTemplateId: undefined,
      valueDataSource: undefined,
      useDataText: undefined,
      data: undefined,
      xAxisKey: undefined,
      lineKeys: undefined,
      ...(shouldClearHomeAssistantAction ? { action: undefined } : {}),
    };

    // For DATA_TEXT, revert to TEXT type
    if (component.type === ComponentType.DATA_TEXT) {
      updateComponent(component.id, {
        type: ComponentType.TEXT,
        properties: cleaned,
      });
    } else {
      updateComponent(component.id, { properties: cleaned });
    }

    // Inject confirmation message
    const msg: PropertyMessage = {
      id: generateMessageId(),
      content: `Data connection removed from "${(typeof props.title === 'string' ? props.title : '') || (typeof props.name === 'string' ? props.name : '') || (typeof props.label === 'string' ? props.label : '') || component.type}".`,
      role: 'assistant',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, msg]);
    setRemoveDataModalOpen(false);
  }, [component, updateComponent, setMessages, setRemoveDataModalOpen]);

  // ============================================================================
  // Data Discovery Modal (hook manages state + handlers)
  // ============================================================================
  const dataDiscovery = useDataDiscovery({
    component: component ?? null,
    updateComponent,
    onMessage: (content: string) => {
      const msg: PropertyMessage = {
        id: generateMessageId(),
        content,
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, msg]);
    },
    onSetInput: (value: string) => {
      setInputValue(value);
      inputRef.current?.focus();
    },
    onUpdateLinkedDataText: (chartId: string, valueFields: string[], dateField: string) => {
      const uniqueValueFields = [
        ...new Set(valueFields.map((field) => field.trim()).filter(Boolean)),
      ];
      const seriesFields = uniqueValueFields.length > 0 ? uniqueValueFields : ["value"];

      // Use tree traversal (same as updateLinkedDataText) - don't rely on linkedChartId matching
      const chartComp = getComponent(chartId);
      if (!chartComp?.parentId) return;

      const wrapper = getComponent(chartComp.parentId);
      if (!wrapper) return;

      // Find the header container (sibling of chart, child of wrapper)
      const headerComponent = components.find(c =>
        c.parentId === wrapper.id &&
        c.type === 'flex-container' &&
        (c.id.includes('header') || c.name?.toLowerCase().includes('header'))
      );
      if (!headerComponent) return;

      // Check for value-group container (compound chart structure)
      const valueGroupComponent = components.find(c =>
        c.parentId === headerComponent.id &&
        c.type === 'flex-container' &&
        (c.id.includes('value-group') || c.name?.toLowerCase().includes('value group'))
      );

      // Find DataText components in value-group or directly in header
      const searchParentId = valueGroupComponent?.id || headerComponent.id;
      const dataTextComponents = components.filter(c =>
        c.parentId === searchParentId &&
        c.type === ComponentType.DATA_TEXT
      );

      const dateDataText = dataTextComponents.find((candidate) =>
        isSecondaryChartDataText(candidate, dateField)
      );
      const valueDataTexts = dataTextComponents.filter((candidate) =>
        candidate.id !== dateDataText?.id
      );
      const valueTemplate = valueDataTexts[0] ?? dateDataText;
      const dateTemplate = dateDataText ?? valueTemplate;
      const valueDataTextIds: string[] = [];

      for (let index = 0; index < seriesFields.length; index += 1) {
        const field = seriesFields[index];
        const target = valueDataTexts[index];
        if (target) {
          valueDataTextIds.push(target.id);
          updateComponent(target.id, {
            name: index === 0 ? target.name : `${getChartSeriesFieldName(field)} Value Display`,
            properties: {
              ...target.properties,
              linkedChartId: chartId,
              valueField: field,
              hoverValueField: field,
              showCurrentOnIdle: true,
              value: '--',
            },
          });
          continue;
        }

        const id = `${chartId}-series-value-${uuidv4()}`;
        valueDataTextIds.push(id);
        addComponent({
          ...createSeriesDataTextInput({
            base: valueTemplate,
            chartId,
            field,
            id,
            parentId: searchParentId,
            pageId: chartComp.pageId,
            index,
          }),
        });
      }

      let dateDataTextId = dateDataText?.id;
      if (dateDataText) {
        updateComponent(dateDataText.id, {
          properties: {
            ...dateDataText.properties,
            linkedChartId: chartId,
            valueField: dateField,
            hoverValueField: undefined,
            showCurrentOnIdle: true,
            value: '--',
          },
        });
      } else if (dateTemplate) {
        dateDataTextId = `${chartId}-x-axis-value-${uuidv4()}`;
        addComponent({
          ...createDateDataTextInput({
            base: dateTemplate,
            chartId,
            dateField,
            id: dateDataTextId,
            parentId: searchParentId,
            pageId: chartComp.pageId,
          }),
        });
      }

      const valueGroupChildren = valueGroupComponent?.children ?? headerComponent.children ?? [];
      const managedDataTextIds = new Set([
        ...dataTextComponents.map((dataText) => dataText.id),
        ...valueDataTextIds,
        ...(dateDataTextId ? [dateDataTextId] : []),
      ]);
      const otherChildIds = valueGroupChildren.filter((childId) => !managedDataTextIds.has(childId));
      updateComponent(searchParentId, {
        children: [
          ...valueDataTextIds,
          ...(dateDataTextId ? [dateDataTextId] : []),
          ...otherChildIds,
        ],
      });
    },
  });

  // ============================================================================
  // Style Intelligence Modal
  // ============================================================================
  const styleIntelligence = useStyleIntelligence();
  const mission = useMission();



  // Update generation message in real-time as stage changes
  useEffect(() => {
    if (!currentGenerationMessageId) return;

    if (aiGenerationStage === 'selecting') {
      setMessages(prev => prev.map(msg =>
        msg.id === currentGenerationMessageId
          ? { ...msg, content: 'Selecting best templates...', isStreaming: true }
          : msg
      ));
    } else if (aiGenerationStage === 'generating') {
      setMessages(prev => prev.map(msg =>
        msg.id === currentGenerationMessageId
          ? { ...msg, content: 'Generating components...', isStreaming: true }
          : msg
      ));
    }
  }, [aiGenerationStage, currentGenerationMessageId]);

  // Reset messages when switching between canvas and component (or between different components)
  // Without this, old canvas messages persist when a component is selected, preventing the
  // component welcome message from appearing (since it only triggers on messages.length === 0)
  const contextKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const contextKey = isCanvas ? 'canvas' : component?.id || null;
    if (contextKeyRef.current !== null && contextKey !== contextKeyRef.current) {
      setMessages([]);
    }
    contextKeyRef.current = contextKey;
  }, [isCanvas, component?.id]);

  // Initialize with a project-aware welcome message
  useEffect(() => {
    if (isCanvas && messages.length === 0) {
      const canvasEmpty = components.length === 0;
      if (canvasEmpty) {
        // Check project state and show context-appropriate welcome
        (async () => {
          let hasKnowledge = false;
          let hasSchemas = false;
          let domain = 'your app';
          let kbTitle = '';

          // Quick check: sessionStorage (same-session, instant)
          try {
            const guidedFlow = sessionStorage.getItem('terramatrix_guided_flow');
            if (guidedFlow) {
              const raw: unknown = JSON.parse(guidedFlow);
              const result = GuidedFlowSchema.safeParse(raw);
              if (result.success) {
                domain = result.data.domain || domain;
                kbTitle = result.data.kbTitle || '';
                if (result.data.lastStep) hasKnowledge = true;
                if (result.data.lastStep === 'data' || result.data.lastStep === 'schema') hasSchemas = true;
              }
            }
          } catch { /* ignore parse errors */ }

          // Cross-session check: API calls (persisted state)
          if (!hasKnowledge) {
            try {
              const { getArticles } = await import('@/services/api/knowledgeApi');
              const articles = await getArticles(
                { limit: 1 },
                { skipAuthRecovery: true, suppressErrorLog: true },
              );
              if (articles.data && articles.data.length > 0) {
                hasKnowledge = true;
                kbTitle = kbTitle || articles.data[0].title || '';
              }
            } catch { /* knowledge service may be down */ }
          }

          // Check for existing schemas (may have been created via UserAgent)
          if (hasKnowledge && !hasSchemas) {
            try {
              const { schemaApi } = await import('@/services/api/schemaApi');
              const schemas = await schemaApi.listSchemas({ limit: 1, status: 'published' });
              if (schemas.data?.length > 0) {
                hasSchemas = true;
                // Update sessionStorage so future checks are instant
                try {
                  const rawFlow: unknown = JSON.parse(sessionStorage.getItem('terramatrix_guided_flow') || '{}');
                  const flowResult = GuidedFlowSchema.safeParse(rawFlow);
                  const flow = flowResult.success ? flowResult.data : {};
                  sessionStorage.setItem('terramatrix_guided_flow', JSON.stringify({ ...flow, lastStep: 'data' }));
                } catch { /* non-fatal */ }
              }
            } catch { /* data service may be down */ }
          }

          let content: string;
          let suggestions: string[];

          if (hasKnowledge && hasSchemas) {
            // Knowledge + schemas exist — suggest Pages & UI
            const kbRef = kbTitle ? ` "${kbTitle}"` : '';
            content =
              `Welcome back! Great progress on your project${kbRef}.\n\n` +
              `1. ~~Knowledge Base~~ — Done!\n` +
              `2. ~~Data Schema~~ — Done!\n` +
              `3. **Pages & UI** — Design your interface\n` +
              `4. **Workflows** — Set up agents, automations and business logic\n\n` +
              `The next step is to **design your pages and UI** — the screens your users will interact with.`;
            suggestions = [
              `Build a dashboard page for ${domain}`,
              `Create a data entry form for ${domain}`,
              `Set up a workflow for ${domain}`,
            ];
          } else if (hasKnowledge) {
            // Knowledge exists — suggest data schema
            const kbRef = kbTitle ? ` "${kbTitle}"` : '';
            content =
              `Welcome back! Your project already has a knowledge base${kbRef}.\n\n` +
              `Here's what you can do next:\n\n` +
              `1. ~~Knowledge Base~~ — Done!\n` +
              `2. **Data Schema** — Define the data your app will manage\n` +
              `3. **Pages & UI** — Design your interface\n` +
              `4. **Workflows** — Set up agents, automations and business logic\n\n` +
              `What would you like to work on?`;
            suggestions = [
              `Create a data schema for ${domain}`,
              `Build a page for ${domain}`,
              'Add to the knowledge base',
              'Help me explore ideas',
            ];
          } else {
            // Fresh project — show full guided flow
            content =
              `Welcome to your project! I can help you build your app step by step.\n\nHere's the recommended flow:\n\n` +
              `1. **Knowledge Base** — Tell me about your idea so I understand the domain\n` +
              `2. **Data Schema** — Define the data your app will manage\n` +
              `3. **Pages & UI** — Design your interface\n` +
              `4. **Workflows** — Set up agents, automations and business logic\n\n` +
              `What would you like to build?`;
            suggestions = [
              'I want to build an app for...',
              "I already know what I want — let's start building",
              'Help me explore ideas',
            ];
          }

          setMessages([{
            id: generateMessageId(),
            content,
            role: 'assistant',
            timestamp: new Date(),
            metadata: { source: 'agent' },
            suggestions,
          }]);
        })();
        return; // Early return — async handler sets messages
      }
    } else if (component && messages.length === 0) {
      const props = component.properties;
      const semanticRole = typeof props._semanticRole === 'string' ? props._semanticRole : undefined;
      const isTemplateComponent = props._templateInstanceId !== undefined;

      let content = `I can help you modify this ${component.type} component`;
      if (semanticRole) {
        content = `I can help you modify the ${semanticRole}`;
      }

      // Icon-specific suggestions
      if (component.type === ComponentType.ICON) {
        content += `. Try commands like:\n• **/icon Heart** - Change icon\n• **/icon-size 48** - Set icon size\n• **/icon-color #3b82f6** - Change color\n• **/icon-rotate 45** - Rotate icon\n• **/icon-library radix** - Switch library`;
      }
      // Gauge-specific welcome (brief - action card provides the interactive UI)
      else if (component.type === ComponentType.GAUGE_CHART || component.type === ComponentType.VERTICAL_GAUGE_CHART) {
        const gaugeType = component.type === ComponentType.VERTICAL_GAUGE_CHART ? 'vertical gauge' : 'gauge';
        content = `I can help you configure this ${gaugeType}. Use the actions below or type a command.`;
      }
      // Radial dial welcome
      else if (component.type === ComponentType.RADIAL_DIAL) {
        content = `I can help you configure this radial dial. Try "set range 0 to 255", "theme ambient", or "set unit to lux". Use the actions below for quick setup.`;
      }
      // Toggle switch welcome
      else if (component.type === ComponentType.TOGGLE_SWITCH) {
        content = `I can help you configure this toggle switch. Try "turn on", "turn off", "theme material", or "set on color to green". Use the actions below for quick setup.`;
      }
      // Status light welcome
      else if (component.type === ComponentType.STATUS_LIGHT) {
        content = `I can help you configure this status light. Try "set status to warning", "blink fast", "theme glow", or "shape diamond". Use the actions below for quick setup.`;
      }
      // Push button welcome
      else if (component.type === ComponentType.PUSH_BUTTON) {
        content = `I can help you configure this push button. Try "theme neon", "set shape to circular", "glow orange", or "size large". Use the actions below for quick setup.`;
      }
      // Icon button welcome
      else if (component.type === ComponentType.ICON_BUTTON) {
        content = `I can help you configure this icon button. Try "set icon to Lightbulb", "theme ghost", "active color blue", or "show label".`;
      }
      // Stepper welcome
      else if (component.type === ComponentType.STEPPER) {
        content = `I can help you configure this stepper. Try "set range 0 to 255", "step 5", "unit degrees", or "theme rounded".`;
      }
      // Slider welcome
      else if (component.type === ComponentType.SLIDER) {
        content = `I can help you configure this slider. Try "set range 0 to 255", "make vertical", "show ticks", or "theme gradient". Use the actions below for quick setup.`;
      }
      // Level Indicator welcome
      else if (component.type === ComponentType.LEVEL_INDICATOR) {
        content = `I can help you configure this level indicator. Try "set segments to 10", "warning at 30", "critical at 10", or "theme battery". Use the actions below for quick setup.`;
      }
      // Segmented Control welcome
      else if (component.type === ComponentType.SEGMENTED_CONTROL) {
        content = `I can help you configure this segmented control. Try "set options Auto Cool Heat Fan", "select Heat", "theme pill", or "add option Dry".`;
      }
      // Color Picker welcome
      else if (component.type === ComponentType.COLOR_PICKER) {
        content = `I can help you configure this color picker. Try "set color to #FF6600", "mode strip", "show brightness slider", or "warm white".`;
      }
      // Thermostat welcome
      else if (component.type === ComponentType.THERMOSTAT) {
        content = `I can help you configure this thermostat. Try "set target to 72", "mode cool", "use celsius", "show humidity", or "theme neon".`;
      }
      // Map-specific suggestions
      else if (component.type === ComponentType.MAP_CHART) {
        content = `I can help you customize this map. Try commands like:\n• "Place a pin on the map"\n• "Lock location" to save the current view\n• "Create group Field A"\n• "Satellite view"`;
      }
      // Chart-specific suggestions
      else if (component.type.includes('chart')) {
        content += `. Try commands like:\n• "Change to bar chart"\n• "Show last 7 days"\n• "Update to hourly data"`;
      } else {
        content += `. Try commands like:\n• "Make the text bigger"\n• "Change color to blue"\n• "Set width to 300px"\n• "Center align the content"`;
      }

      if (isTemplateComponent && semanticRole) {
        content += `\n\nYou can also refer to other parts of this template like "the sidebar", "the main content", etc.`;
      }

      setMessages([{
        id: generateMessageId(),
        content,
        role: 'assistant',
        timestamp: new Date(),
      }]);
    } else if (multipleComponents && multipleComponents.length > 0 && messages.length === 0) {
      setMessages([{
        id: generateMessageId(),
        content: `I can help you modify these ${multipleComponents.length} selected components. Try commands like:\n• "Make them all the same size"\n• "Apply consistent spacing"\n• "Change all colors to match"`,
        role: 'assistant',
        timestamp: new Date(),
      }]);
    }
  }, [component, multipleComponents, isCanvas, messages.length, components.length]);


  // Keyboard shortcuts (Ctrl+K to focus input, Escape to blur)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+K or Cmd+K to focus AI chat input
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        const activeElement = document.activeElement;
        if (
          activeElement?.tagName === 'INPUT' ||
          activeElement?.tagName === 'TEXTAREA' ||
          (isHTMLElement(activeElement) && activeElement.contentEditable === 'true')
        ) {
          return;
        }

        event.preventDefault();
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }

      // Escape key to blur input
      if (event.key === 'Escape') {
        if (document.activeElement === inputRef.current) {
          inputRef.current?.blur();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Plan Mode keyboard shortcut: Shift+Tab (only when chat input is focused)
  useEffect(() => {
    const handlePlanModeShortcut = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && e.shiftKey && document.activeElement === inputRef.current) {
        e.preventDefault();
        planStore.toggle();
      }
    };
    window.addEventListener('keydown', handlePlanModeShortcut);
    return () => window.removeEventListener('keydown', handlePlanModeShortcut);
  }, [planStore]);

  // Plan Mode onboarding tooltip (first activation only)
  useEffect(() => {
    if (planStore.isActive && !localStorage.getItem('planModeOnboardingComplete')) {
      localStorage.setItem('planModeOnboardingComplete', 'true');
    }
  }, [planStore.isActive]);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Auto-speak the latest assistant message via native TTS
  const lastMessageCountRef = useRef(messages.length);
  useEffect(() => {
    if (messages.length > lastMessageCountRef.current) {
      const latest = messages[messages.length - 1];
      if (latest?.role === 'assistant' && latest.content) {
        // Strip markdown/HTML for cleaner speech
        const plainText = latest.content.replace(/[#*_[\]()`]/g, '').trim();
        if (plainText.length > 0 && plainText.length < 500) {
          handleSpeakResponse(plainText);
        }
      }
    }
    lastMessageCountRef.current = messages.length;
  }, [messages, handleSpeakResponse]);

  // Auto-send command helper for pin action card drill-down
  const autoSendCommand = useCallback((command: string, styleWriteTarget: ColorSchemeWriteTarget = 'both') => {
    pendingAutoSendRef.current = command;
    pendingAutoSendStyleWriteTargetRef.current = styleWriteTarget;
    setInputValue(command);
  }, []);

  // Phase 1: Route unclassified queries to the Agent Service (see useAgentMessaging)

  // resetAgentThread is managed by useConversationPersistence above

  // Phase 5: Fast regex pre-check — see useAgentMessaging
  // Helper to persist a message to the store
  const persistMessage = useCallback(async (message: PropertyMessage) => {
    if (!storeInitialized || !tokens?.accessToken) return;

    // Auto-create conversation if one doesn't exist
    let conversationId = activeConversationId;
    if (!conversationId) {
      try {
        conversationId = await createConversation(undefined, project?.id);
      } catch {
        return;
      }
    }

    const storeMsg: Omit<StoreChatMessage, 'id' | 'timestamp'> = {
      content: message.content,
      role: message.role,
      isStreaming: message.isStreaming,
      context: message.context,
      componentData: message.componentData,
      layoutData: message.layoutData,
      applied: message.applied,
      propertyChanges: message.propertyChanges,
      metadata: message.metadata,
    };
    addStoreMessage(storeMsg);
  }, [storeInitialized, tokens?.accessToken, activeConversationId, addStoreMessage, createConversation, project?.id]);

  // Plan Mode orchestration hook — all pipeline logic
  const planOrchestrator = usePlanOrchestration({
    setMessages, persistMessage, setInputValue, setIsLoading,
    generateMessageId,
    communityId: userCommunity?.community_id,
  });

  // Core agent messaging hook (send/stop/callbacks/applyChanges)
  const {
    handleSendMessage,
    handleStopGeneration,
    applyPropertyChanges,
    handlePlaceComponent,
    handleApplyLayout,
  } = useAgentMessaging({
    inputValue, setInputValue,
    isLoading, setIsLoading,
    setError,
    messages, setMessages,
    currentGenerationMessageId, setCurrentGenerationMessageId,
    setShowAutocomplete,
    component, multipleComponents, isCanvas,
    agentThreadIdRef, agentVisualThreadIdRef, userAgentThreadIdRef,
    storeInitialized, resetAgentThread,
    streamingActionCountRef, pendingConfirmationActionsRef,
    setStreamingSteps, setStreamingMessage, setIsStreamingActive,
    setMultiPageProgress, setPendingConfirmation, setPendingChangeSummary,
    setProactiveSuggestions,
    agentStatus, setAgentStatus,
    project, tokens, storePages, currentPageId, components,
    updateComponent, addComponent, generatePageFromDescription,
    selectMultipleComponents, resizeComponent, moveComponent,
    canvasSettings, updateCanvasBackground, updateGridSettings,
    updateCanvasLayout, updateCanvasTheme, updateResponsiveSettings,
    aiGenerationStage, aiGenerationError, aiGenerationMetadata,
    setPendingClarification,
    addToHistory, getLastSuccessfulCommand,
    userCommunity, tradeScope,
    isSlashCommand, executeSlashCommand,
    planOrchestrator,
    dataDiscovery,
    persistMessage, generateMessageId,
    currentMentionContextRef, pendingAutoSendRef, inputRef,
  });

  // Clarification dialog handlers
  const { handleClarificationResponse, handleClarificationCancel } = useClarificationFlow({
    pendingClarification, clearPendingClarification,
    addClarificationToHistory, setMessages, setIsLoading,
    setInputValue, generateMessageId, applyPropertyChanges,
    persistMessage,
  });


  // handleSendMessageRef is declared above (hoisted before useAIChatState); keep current in sync
  handleSendMessageRef.current = handleSendMessage;

  useEffect(() => {
    if (pendingAutoSendRef.current && inputValue === pendingAutoSendRef.current) {
      pendingAutoSendRef.current = null;
      const previousTarget = useEditorStore.getState().styleWriteTarget;
      const nextTarget = pendingAutoSendStyleWriteTargetRef.current ?? 'both';
      pendingAutoSendStyleWriteTargetRef.current = null;
      useEditorStore.getState().setStyleWriteTarget(nextTarget);
      const sendPromise = handleSendMessageRef.current?.();
      if (!sendPromise) {
        useEditorStore.getState().setStyleWriteTarget(previousTarget);
        return;
      }
      void sendPromise.finally(() => {
        useEditorStore.getState().setStyleWriteTarget(previousTarget);
      });
    }
  }, [inputValue]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      // If mention autocomplete is showing, let it handle Enter
      if (showMentionAutocomplete) {
        return; // Document-level listener in MentionAutocomplete handles it
      }

      // If slash autocomplete is showing with valid suggestions, let CommandAutocomplete handle Enter
      if (showAutocomplete) {
        const segments = slashCommandSystem.getProgressiveSegments(inputValue.trim());
        if (segments.length > 0) {
          // Don't send - let the document-level listener in CommandAutocomplete select the command
          return;
        }
      }

      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart ?? value.length;

    setInputValue(value);
    setCursorPosition(cursorPos);

    // Priority 1: Slash commands (must start with /)
    if (value.startsWith('/')) {
      setShowAutocomplete(true);
      setShowMentionAutocomplete(false);
      setActiveMentionContext(null);
      return;
    }
    setShowAutocomplete(false);

    // Priority 2: Mention detection at cursor
    detectAndUpdateMention(value, cursorPos);
  };

  const handleSelectCommand = (command: string) => {
    setInputValue(command);
    setShowAutocomplete(false);
    inputRef.current?.focus();
  };


  const handleClearChat = () => {
    setError(null);
    // Clear any pending clarification
    clearPendingClarification();
    // Phase 3: Reset agent thread for clean conversation start
    resetAgentThread();
    // If we have an active conversation in the store, create a new one
    if (isCanvas && storeInitialized && activeConversationId) {
      createConversation(undefined, project?.id).catch(() => undefined);
    }
    // Reset plan mode if active
    if (planStore.isActive) {
      planStore.reset();
    }
    // Inject contextual guidance after clearing
    const canvasEmpty = components.length === 0;
    if (isCanvas && canvasEmpty) {
      setMessages([{
        id: generateMessageId(),
        role: 'assistant',
        timestamp: new Date(),
        metadata: { source: 'agent' },
        content:
          `Chat cleared. Let's start fresh!\n\nHere's the recommended flow for building your app:\n\n` +
          `1. **Knowledge Base** — Tell me about your idea\n` +
          `2. **Data Schema** — Define your data structures\n` +
          `3. **Pages & UI** — Design the interface\n` +
          `4. **Workflows** — Set up agents, automations and business logic\n\n` +
          `What would you like to build?`,
        suggestions: [
          'I want to build an app for...',
          "Let's start building",
          'Help me explore ideas',
        ],
      }]);
    } else if (isCanvas) {
      setMessages([{
        id: generateMessageId(),
        role: 'assistant',
        timestamp: new Date(),
        metadata: { source: 'agent' },
        content: `Chat cleared. What would you like to work on?`,
        suggestions: [
          'Modify the selected component',
          'Add a new page',
          'Create a workflow',
        ],
      }]);
    } else {
      setMessages([]);
    }
  };

  // Checkpoint navigation shortcuts — only active when a checkpoint is displayed
  useEffect(() => {
    if (!isAtCheckpoint) return;

    const handleCheckpointKey = (e: KeyboardEvent) => {
      if (isHTMLElement(e.target) && ['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          planOrchestrator.handleQualityGate('proceed');
          break;
        case 'Escape':
          e.preventDefault();
          planOrchestrator.handleQualityGate('discard');
          break;
        case 'm': case 'M':
          e.preventDefault();
          planOrchestrator.handleQualityGate('revise');
          break;
        case 's': case 'S':
          e.preventDefault();
          planOrchestrator.handleQualityGate('skip');
          break;
      }
    };

    window.addEventListener('keydown', handleCheckpointKey);
    return () => window.removeEventListener('keydown', handleCheckpointKey);
  }, [isAtCheckpoint, planOrchestrator]);

  // Action card injection: single hook replacing pin + gauge useEffects
  useActionCardInjection({
    component,
    setMessages,
    generateMessageId,
    persistMessage,
  });

  // Show canvas or component UI conditionally
  if (!isCanvas && !component && (!multipleComponents || multipleComponents.length === 0)) {
    return (
      <div className={cn("flex flex-col h-full p-4", className)}>
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Select a component to modify its properties with AI</p>
          </div>
        </div>
      </div>
    );
  }

  // Unified AI chat interface for both canvas and components
  const isEditingCanvas = isCanvas;
  const placeholder = isEditingCanvas
    ? "Describe canvas changes (e.g., 'twitter blue', 'minimal design', 'make it pop')..."
    : "Describe the changes you want...";
  return (
    <div className={cn("flex flex-col h-full overflow-hidden bg-background", className)}>
      {/* Header */}
      <div
        className="flex-shrink-0 bg-card"
        style={{
          padding: '8px 16px 16px 16px',
          borderBottom: '1px solid hsl(var(--border))',
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3
              className="text-foreground"
              style={{
                fontSize: '14px',
                fontWeight: 600,
                margin: 0,
              }}
            >
              {isEditingCanvas ? 'AI Assistant' : 'AI Property Editor'}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {/* Conversation History Popover */}
            <Popover
              open={conversationPopoverOpen}
              onOpenChange={(open) => {
                setConversationPopoverOpen(open);
                if (open && storeInitialized) loadConversations(project?.id);
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "min-h-[32px] px-2 py-1",
                    conversationPopoverOpen ? "text-primary bg-primary/10" : "text-muted-foreground"
                  )}
                  aria-label="Conversation history"
                  data-testid="conversation-history-toggle"
                >
                  <History className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="end" className="w-80 p-0" sideOffset={8}>
                <div
                  className="flex items-center justify-between px-3 py-2 border-b border-border"
                >
                  <span className="text-sm font-medium text-foreground">
                    Conversations
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={() => {
                      createConversation(undefined, project?.id);
                      setConversationPopoverOpen(false);
                    }}
                  >
                    <Plus className="w-3 h-3" />
                    New Chat
                  </Button>
                </div>
                <ScrollArea className="max-h-[300px]">
                  {conversations.length === 0 ? (
                    <p className="text-xs p-4 text-center text-muted-foreground">
                      No conversations yet
                    </p>
                  ) : (
                    conversations.map((conv) => (
                      <button
                        key={conv._id}
                        onClick={() => {
                          switchConversation(conv._id);
                          setConversationPopoverOpen(false);
                        }}
                        className="w-full text-left px-3 py-2.5 hover:bg-muted transition-colors border-b border-border"
                        style={{
                          borderLeft: activeConversationId === conv._id
                            ? '3px solid hsl(var(--primary))'
                            : '3px solid transparent',
                        }}
                      >
                        <p className="text-sm truncate text-foreground">
                          {conv.title || 'Untitled'}
                        </p>
                        <p className="text-[10px] mt-0.5 text-muted-foreground">
                          {(() => {
                            const d = new Date(conv.updatedAt);
                            const diff = Date.now() - d.getTime();
                            const mins = Math.floor(diff / 60000);
                            if (mins < 1) return 'Just now';
                            if (mins < 60) return `${mins}m ago`;
                            const hrs = Math.floor(mins / 60);
                            if (hrs < 24) return `${hrs}h ago`;
                            const days = Math.floor(hrs / 24);
                            return `${days}d ago`;
                          })()}
                          {conv.messageCount > 0 && ` \u00B7 ${conv.messageCount} msgs`}
                        </p>
                      </button>
                    ))
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearChat}
                className="text-xs text-muted-foreground min-h-[32px] px-2 py-1"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
        {component && (
          <p className="text-xs text-muted-foreground mt-1 mb-0">
            Editing: {component.name || `${component.type} component`}
          </p>
        )}
        {multipleComponents && multipleComponents.length > 0 && (
          <p className="text-xs text-muted-foreground mt-1 mb-0">
            Editing: {multipleComponents.length} components
          </p>
        )}
      </div>

      {/* Error display */}
      {error && (
        <Alert className="m-3 flex-shrink-0 border-destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Messages */}
      <ChatMessageList
        scrollAreaRef={scrollAreaRef}
        messages={messages}
        component={component ?? null}
        messageFeedback={messageFeedback}
        setMessageFeedback={setMessageFeedback}
        setMessages={setMessages}
        storePages={storePages}
        currentPageId={currentPageId}
        generateMessageId={generateMessageId}
        handlePlaceComponent={handlePlaceComponent}
        handleApplyLayout={handleApplyLayout}
        autoSendCommand={autoSendCommand}
        setInputValue={setInputValue}
        inputRef={inputRef}
        dataDiscovery={dataDiscovery}
        setRemoveDataModalOpen={setRemoveDataModalOpen}
        setIconPickerOpen={setIconPickerOpen}
        onOpenMapTemplates={onOpenMapTemplates}
        setColorCommandPrefix={setColorCommandPrefix}
        setColorPropertyLabel={setColorPropertyLabel}
        setColorPickerOpen={setColorPickerOpen}
        setDeviceBindingOpen={setDeviceBindingOpen}
        homeAssistantBindingOpen={homeAssistantBindingOpen}
        setHomeAssistantBindingOpen={setHomeAssistantBindingOpen}
        updateComponent={updateComponent}
        duplicateComponent={duplicateComponent}
        pendingClarification={pendingClarification}
        handleClarificationResponse={handleClarificationResponse}
        handleClarificationCancel={handleClarificationCancel}
        isLoading={isLoading}
        removeDataModalOpen={removeDataModalOpen}
        removeDataSourceLabel={removeDataSourceLabel}
        handleRemoveDataConfirm={handleRemoveDataConfirm}
        iconPickerOpen={iconPickerOpen}
        colorPickerOpen={colorPickerOpen}
        colorPropertyLabel={colorPropertyLabel}
        colorCommandPrefix={colorCommandPrefix}
        deviceBindingOpen={deviceBindingOpen}
        planOrchestrator={planOrchestrator}
        styleIntelligence={styleIntelligence}
      />

      {/* Phase 3: Streaming Progress Indicator */}
      {streamingSteps.length > 0 && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <StreamingProgressIndicator
            steps={streamingSteps}
            statusMessage={streamingMessage || undefined}
            onCancel={() => {
              agentConversationManager.abortStreaming();
              setIsStreamingActive(false);
              setStreamingSteps([]);
              setStreamingMessage(null);
              setMultiPageProgress(null);
            }}
            isActive={isStreamingActive}
            multiPage={multiPageProgress || undefined}
          />
        </div>
      )}

      {/* Phase 3: Confirmation Panel */}
      {pendingConfirmation && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <AgentFeedbackPanel
            response={pendingConfirmation}
            changeSummary={pendingChangeSummary || undefined}
            onApply={() => {
              applyAgentResponse(pendingConfirmation, 'Agent generation (confirmed)');
              setPendingConfirmation(null);
              setPendingChangeSummary(null);
            }}
            onDiscard={() => {
              setPendingConfirmation(null);
              setPendingChangeSummary(null);
            }}
            onModify={() => {
              setPendingConfirmation(null);
              setPendingChangeSummary(null);
              inputRef.current?.focus();
            }}
          />
        </div>
      )}

      {/* Phase 6: Proactive Suggestion Panel */}
      {proactiveSuggestions.length > 0 && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <ProactiveSuggestionPanel
            suggestions={proactiveSuggestions}
            onApply={(actions) => {
              applyAgentResponse({ actions, message: 'Applied suggestion fix', followUpSuggestions: [] }, 'Auto-fix suggestion');
            }}
            onDismiss={(suggestionId) => {
              visualEditorApi.suggestionFeedback(suggestionId, 'dismiss').catch(() => {});
              setProactiveSuggestions(prev => prev.filter(s => s.id !== suggestionId));
            }}
            onFeedback={(suggestionId, action) => {
              visualEditorApi.suggestionFeedback(suggestionId, action).catch(() => {});
            }}
          />
        </div>
      )}

      {/* Plan Mode UI */}
      <PlanModePanel
        planOrchestrator={planOrchestrator}
        roadmapModalOpen={roadmapModalOpen}
        setRoadmapModalOpen={setRoadmapModalOpen}
        detailsModalOpen={detailsModalOpen}
        setDetailsModalOpen={setDetailsModalOpen}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setError={setError}
        generateMessageId={generateMessageId}
        setMessages={setMessages}
        persistMessage={persistMessage}
        inputRef={inputRef}
      />

      {/* Input - ChatGPT-style PromptBox */}
      <div
        className="flex-shrink-0 bg-background"
        style={{
          padding: '12px 16px 16px',
        }}
      >

        {/* Plan Mode indicator bar */}
        {planStore.isActive && (
          <div
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium mb-2 text-primary bg-primary/10 rounded-lg border border-border"
          >
            <ClipboardList className="w-3.5 h-3.5" />
            Plan Mode ({planStore.pipelineMode === 'full' ? 'Full' : 'Quick'})
            {planStore.pipelineStage !== 'idle' && planStore.pipelineStage !== 'conversing' && (
              <span className="opacity-60">
                — {PIPELINE_STAGE_LABELS[planStore.pipelineStage]}
              </span>
            )}
            {(planStore.pipelineStage === 'conversing' || planStore.pipelineStage === 'idle') && (
              <span className="opacity-60">— Describe what you want to build</span>
            )}
            <button
              onClick={() => planStore.deactivate()}
              className="ml-auto hover:opacity-70 transition-opacity text-primary"
              aria-label="Deactivate Plan Mode"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Rounded prompt container */}
        <ChatInputBar
          inputValue={inputValue}
          setInputValue={setInputValue}
          isLoading={isLoading}
          isStreamingActive={isStreamingActive}
          isFocused={isFocused}
          setIsFocused={setIsFocused}
          inputRef={inputRef}
          placeholder={placeholder}
          showAutocomplete={showAutocomplete}
          handleSelectCommand={handleSelectCommand}
          showMentionAutocomplete={showMentionAutocomplete}
          activeMentionContext={activeMentionContext}
          cursorPosition={cursorPosition}
          handleSelectMention={handleSelectMention}
          handleInputChange={handleInputChange}
          handleKeyPress={handleKeyPress}
          handleSendMessage={handleSendMessage}
          handleStopGeneration={handleStopGeneration}
          voiceState={voiceState}
          voiceError={voiceError}
          handleMicPress={handleMicPress}
          handleStopSpeaking={handleStopSpeaking}
          setConversationPopoverOpen={setConversationPopoverOpen}
          setShowSkillsModal={setShowSkillsModal}
          setShowAgentsModal={setShowAgentsModal}
          setShowDebugSuite={setShowDebugSuite}
          openKnowledgeModal={openKnowledgeModal}
          setRoadmapModalOpen={setRoadmapModalOpen}
          canUsePlatformAdminDebug={platformAdminEntitlement}
          dataDiscovery={dataDiscovery}
          mission={mission}
          styleIntelligence={styleIntelligence}
        />
      </div>

      <AIChatModals
        showEnrollment={showEnrollment}
        setShowEnrollment={setShowEnrollment}
        onEnrollmentComplete={onEnrollmentComplete}
        aiGenerationStage={aiGenerationStage}
        aiGenerationError={aiGenerationError}
        resetAIGenerationState={resetAIGenerationState}
        showSkillsModal={showSkillsModal}
        setShowSkillsModal={setShowSkillsModal}
        handleSkillInsert={handleSkillInsert}
        showAgentsModal={showAgentsModal}
        setShowAgentsModal={setShowAgentsModal}
        mission={mission}
        showDebugSuite={showDebugSuite}
        setShowDebugSuite={setShowDebugSuite}
        canUsePlatformAdminDebug={platformAdminEntitlement}
      />
    </div>
  );
};
